/* import __COLOCATED_TEMPLATE__ from './channel-group-selector.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { type GuidelineCategory } from 'embercom/models/ai-agent/guidelines';

interface Args {
  targetChannels: string[];
  onChange: (channels: string[]) => void;
  disabled?: boolean;
  category?: GuidelineCategory;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

const CHANNEL_GROUPS = ['chat', 'email', 'voice'] as const;
type ChannelGroup = (typeof CHANNEL_GROUPS)[number];

export const VOICE_UNSUPPORTED_CATEGORIES: GuidelineCategory[] = [
  'content',
  'spam_detection',
  'other',
  'copilot',
  'routing',
  'voice_routing',
];

export default class ChannelGroupSelector extends Component<Signature> {
  @service intl!: IntlService;

  private labelForGroup(group: ChannelGroup): string {
    if (group === 'chat') {
      return this.intl.t('ai-agent.guidance.channel-groups.chat');
    } else if (group === 'email') {
      return this.intl.t('ai-agent.guidance.channel-groups.email');
    } else {
      return this.intl.t('ai-agent.guidance.channel-groups.voice');
    }
  }

  get isVoiceDisabled(): boolean {
    return this.args.category ? VOICE_UNSUPPORTED_CATEGORIES.includes(this.args.category) : false;
  }

  private get effectiveTargetChannels(): string[] {
    let target = this.args.targetChannels;
    if ((!target || target.length === 0) && this.isVoiceDisabled) {
      return ['chat', 'email'];
    }
    return target || [];
  }

  get allChannelsSelected(): boolean {
    if (this.isVoiceDisabled) {
      return false;
    }
    return !this.args.targetChannels || this.args.targetChannels.length === 0;
  }

  get channelItems() {
    let effective = this.effectiveTargetChannels;
    return CHANNEL_GROUPS.map((group) => ({
      text: this.labelForGroup(group),
      value: group,
      isSelected: !this.allChannelsSelected && (effective.includes(group) ?? false),
      ...(group === 'voice' && this.isVoiceDisabled
        ? {
            isDisabled: true,
            tooltipText: this.intl.t('ai-agent.guidance.channel-groups.coming-soon-voice'),
          }
        : {}),
    }));
  }

  get displayText(): string {
    let selected = this.effectiveTargetChannels;
    if (selected.length === 0 || selected.length === CHANNEL_GROUPS.length) {
      return this.intl.t('ai-agent.guidance.channel-groups.all-channels');
    }
    return selected.map((group) => this.labelForGroup(group as ChannelGroup)).join(', ');
  }

  @action
  selectAllChannels() {
    if (this.isVoiceDisabled) {
      return;
    }
    this.args.onChange([]);
  }

  @action
  toggleChannel(channel: string) {
    if (channel === 'voice' && this.isVoiceDisabled) {
      return;
    }

    let current = this.args.targetChannels || [];
    if (current.includes(channel)) {
      this.args.onChange(current.filter((c) => c !== channel));
    } else {
      this.args.onChange([...current, channel]);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::ChannelGroupSelector': typeof ChannelGroupSelector;
    'ai-agent/guidance/channel-group-selector': typeof ChannelGroupSelector;
  }
}
